import { useLayoutEffect, useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Link } from "@/lib/router-compat";
import { Search, ImageOff } from "lucide-react";
import type { RVUnit } from "@/hooks/useInventory";
import { useOptimizedImg } from "@/hooks/useOptimizedImg";

interface Props {
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  loading: boolean;
  query: string;
  items: RVUnit[];
  totalCount: number;
  onShowAll: () => void;
  onClose: () => void;
}

/**
 * Anchored type-ahead dropdown for the inventory search box.
 * Rendered via a portal so it overlays page content (the hero section uses
 * overflow-hidden, which would clip an inline dropdown). Position tracks the
 * search input on scroll/resize so it never moves the page layout.
 */
const SearchSuggestDropdown = ({
  anchorRef,
  open,
  loading,
  query,
  items,
  totalCount,
  onShowAll,
  onClose,
}: Props) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Track the anchor's viewport position while open
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(Math.max(r.width, 280), window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPos({ top: r.bottom + 6, left, width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);

  // Close on outside click / tap
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (contentRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;
  // Anchor scrolled off-screen — hide rather than float detached
  const anchorRect = anchorRef.current?.getBoundingClientRect();
  if (anchorRect && anchorRect.bottom < 0) return null;

  return createPortal(
    <div
      ref={contentRef}
      className="fixed z-[100] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl"
      style={{ top: pos.top, left: pos.left, width: pos.width }}
      role="listbox"
      id="inventory-search-suggestions"
      aria-label="Matching units"
    >
      {loading ? (
        <div className="px-4 py-3 text-sm text-muted-foreground">Searching…</div>
      ) : items.length === 0 ? (
        <div className="px-4 py-3 text-sm text-muted-foreground">
          No matches for <span className="font-semibold text-foreground">“{query}”</span>
        </div>
      ) : (
        <>
          <ul className="max-h-[320px] overflow-y-auto py-1">
            {items.map((rv) => {
              const label =
                [rv.make, rv.model].filter(Boolean).join(" ").trim() || rv.title;
              return (
                <li key={rv.id} role="option" aria-selected={false}>
                  <Link
                    to={`/product/${rv.slug}`}
                    onClick={onClose}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted focus:bg-muted focus:outline-hidden"
                  >
                    <SuggestThumb src={rv.images?.[0] ?? null} alt={label} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground">
                        {label}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {[rv.year, rv.rv_type, rv.stock_number ? `Stock #${rv.stock_number}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            onClick={() => {
              onClose();
              onShowAll();
            }}
            className="flex w-full items-center justify-center gap-2 border-t bg-muted/50 px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <Search className="h-3.5 w-3.5" />
            Show all {totalCount} {totalCount === 1 ? "result" : "results"}
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

/** Small optimized thumbnail for a suggestion row; falls back to an icon. */
const SuggestThumb = ({ src, alt }: { src?: string | null; alt: string }) => {
  const url = useOptimizedImg(src ?? "", "productThumb");
  if (!src) {
    return (
      <span className="flex h-12 w-16 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <ImageOff className="h-4 w-4" aria-hidden />
      </span>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      width={64}
      height={48}
      loading="lazy"
      decoding="async"
      className="h-12 w-16 shrink-0 rounded-md bg-muted object-cover"
    />
  );
};

export default SearchSuggestDropdown;